import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { createTagPageUrl } from 'src/components/Tag/utils'
import type { PromptSegment } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { lorem } from 'src/utils/lorem'
import { normalizeString } from 'src/utils/string'
import { MentionAwareText } from './MentionAwareText'
import type { MentionMediaMap } from './mentionMedia'

const ArtworkPrompts: FC<{
  prompts?: string
  segmentedPrompts?: PromptSegment[]
  mentionMediaMap?: MentionMediaMap
}> = ({ prompts, segmentedPrompts, mentionMediaMap }) => {
  const { t } = useTranslation(['common'])
  const normalizedPrompts = normalizeString(prompts || '')

  if (!prompts)
    return <div className="italic opacity-75">({t('common:not-set')})</div>

  return (
    <h2 className="text-sm font-normal leading-6 opacity-80 break-all">
      {segmentedPrompts ? (
        segmentedPrompts?.map((s, i) => (
          <span key={i}>
            {s.prefix}
            {s.content &&
              (s.tagName ? (
                <Link to={createTagPageUrl('artwork', s.tagName)}>
                  {s.content}
                </Link>
              ) : (
                <MentionAwareText text={s.content} mediaMap={mentionMediaMap} />
              ))}
            {s.suffix}
          </span>
        ))
      ) : (
        <MentionAwareText text={normalizedPrompts} mediaMap={mentionMediaMap} />
      )}
    </h2>
  )
}

export const ArtworkPromptsWithHidden = ({
  prompts,
  segmentedPrompts,
  shouldHide,
  mentionMediaMap,
}: {
  prompts?: string
  segmentedPrompts?: PromptSegment[]
  shouldHide?: boolean
  mentionMediaMap?: MentionMediaMap
}) => {
  const { t } = useTranslation(['artwork'])

  return (
    <div
      className={cx(
        'text-sm relative font-medium rounded-lg py-2 px-3 border border-solid border-gray-100 leading-5 mt-4',
        shouldHide
          ? 'h-[4.5rem] overflow-hidden'
          : 'h-auto max-h-[9rem] overflow-auto',
      )}
    >
      {shouldHide ? (
        <>
          <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
            <div className="blur h-full min-h-full select-none overflow-hidden">
              {lorem}
            </div>
            {/* Edge fade so the blur softens into the background */}
            <div
              className="absolute inset-0 pointer-events-none rounded-[inherit] bg-white dark:bg-gray-900"
              style={{
                maskImage:
                  'radial-gradient(ellipse 85% 85% at 50% 50%, transparent 45%, black 95%)',
                WebkitMaskImage:
                  'radial-gradient(ellipse 85% 85% at 50% 50%, transparent 45%, black 95%)',
              }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center font-medium select-none">
            {t('artwork:hidden-by-author')}
          </div>
        </>
      ) : (
        <ArtworkPrompts
          prompts={prompts}
          segmentedPrompts={segmentedPrompts}
          mentionMediaMap={mentionMediaMap}
        />
      )}
    </div>
  )
}

export const ArtworkNegativePrompts = ({
  negativePrompts,
}: {
  negativePrompts?: string
}) => {
  return (
    <div
      className={cx(
        'text-sm relative font-medium rounded-lg h-auto max-h-[9rem] py-2 px-3 border border-solid border-gray-100 overflow-auto leading-5 mt-4',
      )}
    >
      <ArtworkPrompts prompts={negativePrompts} />
    </div>
  )
}
